import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUsers, getRegistrations, approveRegistration, rejectRegistration, PAGE_SIZES, toLimitOffset } from '../services/api';
import { getAdminInfo } from '../services/auth';
import CompanyManagement from './CompanyManagement';
import './UserManagement.css';

function UserManagement() {
  const { t } = useTranslation();
  const [mainTab, setMainTab] = useState('benutzer');
  const [users, setUsers] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('users');
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const adminInfo = getAdminInfo();
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const paging = toLimitOffset(page - 1, pageSize);
      const data = await getUsers(paging);
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message || t('admin.user_management.errors.load_users_failed'));
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [t, page, pageSize]);

  const loadRegistrations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getRegistrations();
      setRegistrations(data.registrations || []);
    } catch (err) {
      setError(err.message || t('admin.user_management.errors.load_registrations_failed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (mainTab === 'benutzer' && activeTab === 'users') {
      loadUsers();
    } else if (mainTab === 'benutzer' && activeTab === 'registrations') {
      loadRegistrations();
    }
  }, [mainTab, activeTab, loadUsers, loadRegistrations]);

  useEffect(() => {
    if (activeTab !== 'users') return;
    const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
    if (page > totalPages) setPage(totalPages);
  }, [page, pageSize, total, activeTab]);

  const handleApprove = async (registrationId, notes) => {
    try {
      await approveRegistration(registrationId, adminInfo?.id, notes);
      loadRegistrations();
    } catch (err) {
      setError(err.message || t('admin.user_management.errors.approve_failed'));
    }
  };

  const handleReject = async (registrationId, notes) => {
    try {
      await rejectRegistration(registrationId, adminInfo?.id, notes);
      loadRegistrations();
    } catch (err) {
      setError(err.message || t('admin.user_management.errors.reject_failed'));
    }
  };

  return (
    <div className="user-management">
      <h2>{t('admin.user_management.title')}</h2>

      <div className="tabs main-tabs">
        <button
          className={mainTab === 'benutzer' ? 'active' : ''}
          onClick={() => setMainTab('benutzer')}
        >
          {t('admin.user_management.main_tabs.users')}
        </button>
        <button
          className={mainTab === 'companies' ? 'active' : ''}
          onClick={() => setMainTab('companies')}
        >
          {t('admin.user_management.main_tabs.companies')}
        </button>
      </div>

      {mainTab === 'companies' && (
        <CompanyManagement embedded />
      )}

      {mainTab === 'benutzer' && (
        <>
      <div className="tabs">
        <button
          className={activeTab === 'users' ? 'active' : ''}
          onClick={() => setActiveTab('users')}
        >
          {t('admin.user_management.tabs.users')}
        </button>
        <button
          className={activeTab === 'registrations' ? 'active' : ''}
          onClick={() => setActiveTab('registrations')}
        >
          {t('admin.user_management.tabs.pending_registrations', { count: registrations.length })}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">{t('admin.user_management.loading')}</div>
      ) : activeTab === 'users' ? (
        <>
        <div className="pagination" style={{ margin: '12px 0', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span>
            {t('common.per_page')}{' '}
            <select
              value={pageSize}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                if (!PAGE_SIZES.includes(n)) return;
                setPageSize(n);
                setPage(1);
              }}
            >
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </span>
          <button type="button" className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage(1)}>
            «
          </button>
          <button type="button" className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            ‹
          </button>
          <span>
            {t('common.pagination_page_of', { page, totalPages, total })}
          </span>
          <button type="button" className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            ›
          </button>
          <button type="button" className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>
            »
          </button>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th className="col-id">{t('admin.user_management.table.users.user_id')}</th>
              <th>{t('admin.user_management.table.users.email')}</th>
              <th>{t('admin.user_management.table.users.name')}</th>
              <th>{t('admin.user_management.table.users.mandantennummer')}</th>
              <th>{t('admin.user_management.table.users.company')}</th>
              <th>{t('admin.user_management.table.users.status')}</th>
              <th>{t('admin.user_management.table.users.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center' }}>
                  {t('admin.user_management.empty.users')}
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id}>
                  <td className="col-id">{user.id}</td>
                  <td>{user.email}</td>
                  <td>{user.name || '-'}</td>
                  <td>{user.mandantennummer || '-'}</td>
                  <td>{user.company || '-'}</td>
                  <td>
                    <span className={`badge ${user.is_approved ? 'badge-success' : 'badge-warning'}`}>
                      {user.is_approved ? t('admin.common.status.approved') : t('admin.common.status.pending')}
                    </span>
                  </td>
                  <td>
                    <Link to={`/admin/users/${user.id}`} className="btn btn-secondary">
                      {t('admin.common.buttons.details')}
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.user_management.table.registrations.email')}</th>
              <th>{t('admin.user_management.table.registrations.name')}</th>
              <th>{t('admin.user_management.table.registrations.company')}</th>
              <th>{t('admin.user_management.table.registrations.requested_at')}</th>
              <th>{t('admin.user_management.table.registrations.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {registrations.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center' }}>
                  {t('admin.user_management.empty.registrations')}
                </td>
              </tr>
            ) : (
              registrations.map((reg) => (
                <tr key={reg.id}>
                  <td>{reg.email}</td>
                  <td>{reg.name || '-'}</td>
                  <td>{reg.company || '-'}</td>
                  <td>{new Date(reg.requested_at).toLocaleDateString('de-DE')}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-success"
                        onClick={() => handleApprove(reg.id, '')}
                      >
                        {t('admin.common.buttons.approve')}
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleReject(reg.id, '')}
                      >
                        {t('admin.common.buttons.reject')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
        </>
      )}
    </div>
  );
}

export default UserManagement;






